import type {
  MultiplayerGameDelta,
  MultiplayerHeroState,
  MultiplayerEnemyState,
  GameEndPayload,
  OpponentDisconnectedPayload,
  PlayerAction,
} from '@roe/shared';
import { MultiplayerService } from '../services/MultiplayerService.js';

const GAME_W = 960;

// ──────────────────────────────────────────────
// Hook interface consumed by BattleScene
// ──────────────────────────────────────────────

export interface BattleSceneMultiplayerHooks {
  onStateUpdate: (delta: MultiplayerGameDelta) => void;
  onGameEnd: (payload: GameEndPayload) => void;
  onOpponentDisconnect: (payload: OpponentDisconnectedPayload) => void;
}

// ──────────────────────────────────────────────
// Mirror helpers
// ──────────────────────────────────────────────

function mirrorHero(h: MultiplayerHeroState): MultiplayerHeroState {
  return { ...h, position: { ...h.position, x: GAME_W - h.position.x } };
}

function mirrorEnemy(e: MultiplayerEnemyState): MultiplayerEnemyState {
  return { ...e, position: { ...e.position, x: GAME_W - e.position.x } };
}

function mirrorDelta(delta: MultiplayerGameDelta): MultiplayerGameDelta {
  return {
    ...delta,
    ...(delta.player1Heroes !== undefined && { player1Heroes: delta.player1Heroes.map(mirrorHero) }),
    ...(delta.player2Heroes !== undefined && { player2Heroes: delta.player2Heroes.map(mirrorHero) }),
    ...(delta.enemies !== undefined && { enemies: delta.enemies.map(mirrorEnemy) }),
  };
}

// ──────────────────────────────────────────────
// Adapter
// ──────────────────────────────────────────────

/**
 * Bridges MultiplayerService socket events to BattleScene callbacks.
 * Handles PVP mirror transform for player 2 (X-axis flip of all positions).
 */
export class MultiplayerBattleAdapter {
  private mpService: MultiplayerService;
  private _adapterListeners: Array<{ event: string; fn: Parameters<typeof MultiplayerService.prototype.offListener>[1] }> = [];

  constructor() {
    this.mpService = MultiplayerService.getInstance();
  }

  // ──────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────

  /**
   * @param hooks  Callbacks for BattleScene to receive game events
   * @param playerIndex  1 = canonical view (no transform), 2 = mirrored (X-axis flip)
   */
  start(hooks: BattleSceneMultiplayerHooks, playerIndex: 1 | 2 = 1): void {
    const shouldMirror = playerIndex === 2;

    const onStateUpdate = (delta: MultiplayerGameDelta) => {
      hooks.onStateUpdate(shouldMirror ? mirrorDelta(delta) : delta);
    };
    const onGameEnd = (payload: GameEndPayload) => {
      hooks.onGameEnd(payload);
    };
    const onOpponentDisconnect = (payload: OpponentDisconnectedPayload) => {
      hooks.onOpponentDisconnect(payload);
    };

    this.mpService.onGameStateUpdate(onStateUpdate);
    this.mpService.onGameEnd(onGameEnd);
    this.mpService.onOpponentDisconnect(onOpponentDisconnect);

    // Track only the listeners registered by this adapter instance
    this._adapterListeners = [
      { event: 'game-state-update', fn: onStateUpdate as Parameters<typeof this.mpService.offListener>[1] },
      { event: 'game-end', fn: onGameEnd as Parameters<typeof this.mpService.offListener>[1] },
      { event: 'opponent-disconnected', fn: onOpponentDisconnect as Parameters<typeof this.mpService.offListener>[1] },
    ];
  }

  stop(): void {
    for (const { event, fn } of this._adapterListeners) {
      this.mpService.offListener(event, fn);
    }
    this._adapterListeners = [];
  }

  // ──────────────────────────────────────────────
  // Action forwarding
  // ──────────────────────────────────────────────

  sendAction(action: PlayerAction): void {
    this.mpService.sendAction(action);
  }
}
